import React from 'react';
import PropTypes from 'prop-types';
import { Dialog } from '@headlessui/react';
import Button from '../Button/Button';
import BaseModal from './BaseModal';

export const ModalHeader = ({ title, description, children, className = '' }) => (
  <div className={`bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4 ${className}`.trim()}>
    {children || (
      <div className="sm:flex sm:items-start">
        <div className="mt-3 text-center sm:mt-0 sm:text-left">
          {title && (
            <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
              {title}
            </Dialog.Title>
          )}
          {description && <p className="mt-2 text-sm text-gray-500">{description}</p>}
        </div>
      </div>
    )}
  </div>
);

ModalHeader.propTypes = {
  title: PropTypes.node,
  description: PropTypes.node,
  children: PropTypes.node,
  className: PropTypes.string
};

export const ModalBody = ({ children, className = '' }) => (
  <div className={`px-4 py-4 sm:px-6 ${className}`.trim()}>{children}</div>
);

ModalBody.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string
};

export const ModalFooter = ({ children, className = '' }) => (
  <div className={`bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 ${className}`.trim()}>
    {children}
  </div>
);

ModalFooter.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string
};

const ICON_TONE_CLASSES = {
  danger: 'bg-red-100 text-red-600',
  warning: 'bg-yellow-100 text-yellow-600',
  neutral: 'bg-gray-100 text-gray-600',
  primary: 'bg-primary-100 text-primary-600'
};

export const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'neutral',
  loading = false,
  loadingLabel,
  size = 'md'
}) => (
  <BaseModal isOpen={isOpen} onClose={onClose} size={size}>
      <ModalHeader title={title} description={description} />
      <ModalFooter>
        <Button
          variant={tone === 'danger' ? 'danger' : 'primary'}
          size="sm"
          onClick={onConfirm}
          disabled={loading}
          loading={loading}
          className="sm:ml-3 sm:w-auto w-full"
        >
          {loading ? loadingLabel || confirmLabel : confirmLabel}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={onClose}
          disabled={loading}
          className="mt-3 sm:mt-0 sm:w-auto w-full"
        >
          {cancelLabel}
        </Button>
      </ModalFooter>
    </BaseModal>
);

ConfirmModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.node.isRequired,
  description: PropTypes.node,
  confirmLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  tone: PropTypes.oneOf(['danger', 'warning', 'neutral', 'primary']),
  loading: PropTypes.bool,
  loadingLabel: PropTypes.string,
  size: PropTypes.string
};

export { ICON_TONE_CLASSES };
